import { c as _c } from "react/compiler-runtime";
import { PropsWithTitle } from "@/types/ui";
import React, { PropsWithChildren } from "react";
import Link from "next/link";
import { cn } from "@lib/utils";

/**
 * FixtureSection is a React functional component used to define a section within a design system.
 * It provides a structured wrapper containing optional children elements and a title.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The child elements to be rendered inside the section.
 * @param {string} props.title - The title of the section.
 * @param {string} [props.id] - An optional id to uniquely identify the section.
 *
 * @returns {JSX.Element} A styled `div` element containing a title and any child elements.
 */
export const FixtureSection = t0 => {
  const $ = _c(20);
  const {
    children,
    title,
    id,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const t2 = variant === "default";
  const t3 = variant === "inner";
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = cn("flex flex-col gap-6 p-6 pb-12 md:gap-8", {
      "rounded-sm border border-dashed border-muted-foreground/60": t2,
      "border-none pl-0": t3
    });
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = variant === "inner";
  let t6;
  if ($[3] !== t5) {
    t6 = cn("flex w-full gap-2 text-sm text-muted-foreground md:text-base", {
      "border-b border-dashed border-muted-foreground/60 pt-4 pb-2": t5
    });
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== id || $[6] !== title) {
    t7 = <h2 id={id} className="scroll-m-10 text-2xl font-semibold tracking-tight text-foreground md:text-3xl">{title}</h2>;
    $[5] = id;
    $[6] = title;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== id) {
    t8 = id && <span className="ml-auto align-top text-sm underline"><Link href={`#${id}`}>{`#${id}`}</Link></span>;
    $[8] = id;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t6 || $[11] !== t7 || $[12] !== t8) {
    t9 = <div className={t6}>{t7}{t8}</div>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== children) {
    t10 = <div className="flex flex-col gap-5 pl-0 text-sm text-foreground/80 md:pl-2 md:text-base [&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-base [&_code]:font-medium">{children}</div>;
    $[14] = children;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t4 || $[18] !== t9) {
    t11 = <div className={t4}>{t9}{t10}</div>;
    $[16] = t10;
    $[17] = t4;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
};